/**
 * Close one punch-list item: it was fixed, or it will not be and that is
 * agreed.
 *
 * Two exits rather than one, because they mean opposite things and the audit
 * trail has to keep them apart. RESOLVE says the work happened. WAIVE says it
 * will not, deliberately - a partial roof, a one-time exception, a contractor
 * doing something different instead. Both close the item, and closing it is
 * what unblocks the claim from settling.
 *
 * A waiver requires a reason. A year later, a waiver with no stated reason is
 * indistinguishable from a mistake, and the person reading it will be trying
 * to work out why a claim settled with work outstanding. The backend enforces
 * this too - the field is not the guard, it is the prompt.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import type { PunchListWorkItem } from '@/admin/hooks/use-inspections'

type CloseAction = 'resolve' | 'waive'

export function ClosePunchListItemDialog({
  item,
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: {
  item: PunchListWorkItem | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (action: CloseAction, text: string) => void | Promise<void>
  isPending: boolean
}) {
  const { t } = useTranslation()
  const [action, setAction] = useState<CloseAction>('resolve')

  const fields: FormField[] = [
    {
      type: 'textarea',
      name: 'text',
      label:
        action === 'resolve'
          ? t('inspections.punchList.notesLabel', 'What was done')
          : t('inspections.punchList.reasonLabel', 'Why it is being waived'),
      value: '',
      placeholder:
        action === 'resolve'
          ? t(
              'inspections.punchList.notesPlaceholder',
              'Optional - what the contractor did to close this out.',
            )
          : t(
              'inspections.punchList.reasonPlaceholder',
              'Required - e.g. partial roof, alternative remediation agreed.',
            ),
      // Required only for a waiver: resolving is self-explanatory, waiving
      // never is.
      ...(action === 'waive'
        ? {
            required: t(
              'inspections.punchList.reasonRequired',
              'A reason is required to waive an item.',
            ),
            validationSchema: z
              .string()
              .trim()
              .min(
                1,
                t(
                  'inspections.punchList.reasonRequired',
                  'A reason is required to waive an item.',
                ),
              ),
          }
        : { validationSchema: z.string().optional() }),
    },
  ]

  const { Form, FormFooter } = useForm(
    {
      name: 'close-punch-list-item',
      submit: 'footer',
      fields,
    },
    async (values) => {
      await onSubmit(action, String((values as { text?: string }).text ?? ''))
    },
    // `action` swaps the field's label, placeholder and validation, so it has
    // to be here by name. No `type:'custom'` render closure in this form, so
    // there is no stale-closure trap - but the same rule applies.
    [open, item?.uuid, action],
  )

  if (!item) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('inspections.punchList.closeTitle', 'Close punch-list item')}
          </DialogTitle>
          <DialogDescription>
            {item.description}
            {item.inspection_code ? ` · ${item.inspection_code}` : ''}
            {item.contractor_name ? ` · ${item.contractor_name}` : ''}
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={action}
          onValueChange={(v: string) => setAction(v as CloseAction)}
        >
          <TabsList variant="line">
            <TabsTrigger value="resolve">
              {t('inspections.punchList.actionResolve', 'Resolved')}
            </TabsTrigger>
            <TabsTrigger value="waive">
              {t('inspections.punchList.actionWaive', 'Waive')}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <p className="text-[12px] text-[var(--ink-3)]">
          {action === 'resolve'
            ? t(
                'inspections.punchList.resolveHint',
                'The work was done. This clears the item and unblocks the claim.',
              )
            : t(
                'inspections.punchList.waiveHint',
                'The work will not be done, and that is agreed. This also unblocks the claim, so the reason is what explains it later.',
              )}
        </p>

        <Form className="flex flex-col gap-4 py-1" />

        {FormFooter && (
          <FormFooter
            onCancel={() => onOpenChange(false)}
            // Stays enabled on an empty required field - clicking validates
            // and highlights it, per the house rule.
            submitText={
              isPending
                ? t('inspections.punchList.closing', 'Closing…')
                : action === 'resolve'
                  ? t('inspections.punchList.actionResolve', 'Resolved')
                  : t('inspections.punchList.actionWaive', 'Waive')
            }
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
